import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, NgZone, OnChanges, OnDestroy, inject, input, viewChild } from '@angular/core';
import { Chart, ChartData, ChartOptions, ChartType } from 'chart.js/auto';

const PLAIN_NUMBER = /^\d+(\.\d+)?$/;

function toCssLength(height: string | undefined): string | undefined {
	return height && PLAIN_NUMBER.test(height) ? `${height}px` : height;
}

@Component({
	selector: 'xp-chart',
	standalone: true,
	templateUrl: './xp-chart.component.html',
	styleUrls: ['./xp-chart.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpChartComponent implements AfterViewInit, OnChanges, OnDestroy {
	type = input.required<ChartType>();
	data = input.required<ChartData>();
	options = input<ChartOptions>({});
	height = input<string | undefined, string | undefined>(undefined, { transform: toCssLength });

	private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
	private readonly zone = inject(NgZone);
	private chart?: Chart;

	ngAfterViewInit(): void {
		this.build();
	}

	ngOnChanges(): void {
		if (this.chart) {
			this.chart.destroy();
			this.build();
		}
	}

	ngOnDestroy(): void {
		this.chart?.destroy();
		this.chart = undefined;
	}

	private build(): void {
		/* Screens share one options object between charts, and Chart.js writes into the options it gets. */
		const options: ChartOptions = { ...this.options(), responsive: true };
		if (this.height()) {
			options.maintainAspectRatio = false;
		}
		/* Chart.js calls these from its own events, outside the zone; a screen handler that changes the page needs the zone. */
		const { onClick, onHover } = options;
		if (onClick) {
			options.onClick = (event, elements, chart) => this.zone.run(() => onClick.call(chart, event, elements, chart));
		}
		if (onHover) {
			options.onHover = (event, elements, chart) => this.zone.run(() => onHover.call(chart, event, elements, chart));
		}
		/* Outside the zone, the animation frames of Chart.js run no change detection. */
		this.zone.runOutsideAngular(() => {
			this.chart = new Chart(this.canvas().nativeElement, { type: this.type(), data: this.data(), options });
		});
	}
}
